import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  CollectionPreferences,
  ColumnLayout,
  Header,
  Link,
  Pagination,
  PropertyFilter,
  SpaceBetween,
  Table,
  TableProps
} from '@amzn/awsui-components-react';
import { useCollection, PropertyFilterQuery } from '@amzn/awsui-collection-hooks';

import { getString, getPropertyFilterString } from '../../common/ui-string-labels/ui-strings-utils';
import _ from 'lodash';
import {
  buildColumnDefinitions,
  buildFilteringProperties,
  buildCollectionPreferencesProps,
  defaultPagingOptions,
  CellTypeRenderers,
  BuildColumnOptions
} from '../../common/utils/table-utils';
import { ColumnSetting } from '../../common/utils/config-models';
import EmptyTableBox from '../../common/EmptyTableBox/EmptyTableBox';
import {
  BackfillItem,
  ContentBackfillService,
  GetRunItemsResponse,
  MembershipVerdict
} from '../../services/content-backfill-service';
import { usePreferences } from '../../hooks/usePreferences';
import { getItemStatusIndicatorType, getItemStatusTooltip } from './backfill-status-config';
import { getValidationBadge, buildCatalogicalUrl } from './backfill-utils';
import itemsTableConfig from './items.table-config.json';

import './BackfillItemsTable.scss';

export interface BackfillItemsTableProps {
  runId: string;
  onRowExpanded?: (sourceSourceId: string) => void;
  onCatalogicalLinkClick?: (itemId: string) => void;
}

const columns: ColumnSetting[] = itemsTableConfig.columns as unknown as ColumnSetting[];
const FILTERING_PROPERTIES = buildFilteringProperties(columns);
const PREFS_CONFIG = buildCollectionPreferencesProps(columns);
const PREFERENCES_KEY = 'BackfillItemsTablePreferences';
const DEFAULT_PAGE_SIZE = 30;

// -- Helper components --------------------------------------------------------

/**
 * Full-width inline detail rendered inside the expanded child row.
 * Uses sticky positioning + JS-measured width to span the visible table area.
 */
export const DetailRowContent: FunctionComponent<{ item: BackfillItem; detailWidth?: number }> = ({ item, detailWidth }) => {
  const style: React.CSSProperties = detailWidth !== undefined && detailWidth > 0
    ? { width: `${detailWidth}px`, boxSizing: 'border-box' }
    : {};
  return (
    <div className="detail-row-container" data-testid={`detail-row-${item.sourceSourceId}`} style={style}>
      <ColumnLayout columns={4} variant="text-grid">
        <SpaceBetween size="xs">
          <Box variant="awsui-key-label">{getString('contentBackfill.itemsTable.expandedDescription')}</Box>
          <Box variant="p">
            {item.generatedDescription || getString('contentBackfill.itemsTable.noDescription')}
          </Box>
        </SpaceBetween>
        <SpaceBetween size="xs">
          <Box variant="awsui-key-label">{getString('contentBackfill.itemsTable.expandedSources')}</Box>
          {item.sources && (item.sources.internal.length > 0 || item.sources.web.length > 0) ? (
            <SpaceBetween size="xxs">
              {item.sources.internal.length > 0 && (
                <Box>
                  <Box variant="awsui-key-label">{getString('contentBackfill.itemsTable.expandedSourcesInternal')}</Box>
                  {item.sources.internal.map((src, idx) => (
                    <Box key={idx} variant="small">{src}</Box>
                  ))}
                </Box>
              )}
              {item.sources.web.length > 0 && (
                <Box>
                  <Box variant="awsui-key-label">{getString('contentBackfill.itemsTable.expandedSourcesWeb')}</Box>
                  {item.sources.web.map((src, idx) => (
                    <Link key={idx} href={src} external variant="primary">{src}</Link>
                  ))}
                </Box>
              )}
            </SpaceBetween>
          ) : (
            <Box variant="small">{getString('contentBackfill.itemsTable.noSources')}</Box>
          )}
        </SpaceBetween>
        <SpaceBetween size="xs">
          <Box variant="awsui-key-label">{getString('contentBackfill.itemsTable.expandedFlags')}</Box>
          {item.validation ? (
            <SpaceBetween size="xxs">
              {item.validation.membership && (
                <SpaceBetween size="xxxs">
                  <Box variant="small">{getString('contentBackfill.itemsTable.expandedMembership')} {getValidationBadge(item.validation.membership.verdict)}</Box>
                  {(item.validation.membership as MembershipVerdict).matchedName && (
                    <Box variant="small" color="text-body-secondary" data-testid="membership-matched-name">
                      {getString('contentBackfill.itemsTable.membershipMatchedPrefix')} {(item.validation.membership as MembershipVerdict).matchedName}
                    </Box>
                  )}
                  {(item.validation.membership as MembershipVerdict).taxonomyId && (
                    <Link
                      href={buildCatalogicalUrl((item.validation.membership as MembershipVerdict).taxonomyId!)}
                      external
                      fontSize="body-s"
                      data-testid="membership-taxonomy-link"
                    >
                      {(item.validation.membership as MembershipVerdict).taxonomyId}
                    </Link>
                  )}
                  {(item.validation.membership as MembershipVerdict).score != null && (
                    <Box variant="small" color="text-body-secondary" data-testid="membership-score">
                      {getString('contentBackfill.itemsTable.membershipScorePrefix')} {Math.round((item.validation.membership as MembershipVerdict).score! * 100)}%
                    </Box>
                  )}
                </SpaceBetween>
              )}
              {item.validation.accuracy && (
                <SpaceBetween size="xxxs">
                  <Box variant="small">{getString('contentBackfill.itemsTable.expandedAccuracy')} {getValidationBadge(item.validation.accuracy.verdict)}</Box>
                  {item.validation.accuracy.reason && (
                    <Box variant="small" color="text-body-secondary" data-testid="accuracy-reason">{item.validation.accuracy.reason}</Box>
                  )}
                </SpaceBetween>
              )}
              {item.validation.classification && (
                <SpaceBetween size="xxxs">
                  <Box variant="small">{getString('contentBackfill.itemsTable.expandedClassification')} {getValidationBadge(item.validation.classification.verdict)}</Box>
                  {item.validation.classification.reason && (
                    <Box variant="small" color="text-body-secondary" data-testid="classification-reason">{item.validation.classification.reason}</Box>
                  )}
                </SpaceBetween>
              )}
              {item.validation.style && (
                <SpaceBetween size="xxxs">
                  <Box variant="small">{getString('contentBackfill.itemsTable.expandedStyle')} {getValidationBadge(item.validation.style.verdict)}</Box>
                  {item.validation.style.violations && item.validation.style.violations.length > 0 && (
                    <Box variant="small" color="text-body-secondary" data-testid="style-violations">{item.validation.style.violations.join(', ')}</Box>
                  )}
                </SpaceBetween>
              )}
            </SpaceBetween>
          ) : (
            <Box variant="small">{getString('contentBackfill.itemsTable.noFlags')}</Box>
          )}
        </SpaceBetween>
        <SpaceBetween size="xs">
          <Box variant="awsui-key-label">{getString('contentBackfill.itemsTable.expandedImportResult')}</Box>
          {item.importResult ? (
            <SpaceBetween size="xxs">
              <Box variant="small">{getString('contentBackfill.itemsTable.expandedImportStatus')} {item.importResult.status}</Box>
              {item.importResult.taxonomyId && (
                <Link
                  href={buildCatalogicalUrl(item.importResult.taxonomyId)}
                  external
                  fontSize="body-s"
                >
                  {item.importResult.taxonomyId}
                </Link>
              )}
              {item.importResult.error && (
                <Box variant="small" color="text-status-error">{item.importResult.error}</Box>
              )}
            </SpaceBetween>
          ) : (
            <Box variant="small">-</Box>
          )}
        </SpaceBetween>
      </ColumnLayout>
    </div>
  );
};

// -- Expandable row type extension --------------------------------------------

/**
 * TableItem extends BackfillItem with detail-row markers.
 * Synthetic detail children have _isDetailRow=true and carry the parent via _parentItem.
 */
export interface TableItem extends BackfillItem {
  _isDetailRow?: boolean;
  _parentItem?: BackfillItem;
}

// -- Filtering function (extracted for testability) ---------------------------

/**
 * PropertyFilter filtering function used by useCollection.
 * Supports free-text search and typed property operators (=, !=, <, <=, >, >=, :, !:).
 * Resolves nested paths (e.g. "confidenceScore.total") via lodash get.
 */
export function itemsFilteringFunction(
  item: BackfillItem,
  query: PropertyFilterQuery
): boolean {
  const { tokens, operation } = query;
  const matcher = (token: { propertyKey?: string; value: any; operator: string }) => {
    const key = token.propertyKey;
    if (!key) {
      // Free-text search across all filterable fields
      const text = String(token.value).toLowerCase();
      return FILTERING_PROPERTIES.some((fp) => {
        const v = _.get(item, fp.key);
        return v !== undefined && v !== null && String(v).toLowerCase().includes(text);
      });
    }
    // Resolve nested paths (e.g. "confidenceScore.total") via lodash get
    const value = _.get(item, key);
    const tokenValue = token.value;
    const operator = token.operator;
    if (value === undefined || value === null) return false;
    const numValue = typeof value === 'number' ? value : Number(value);
    const numToken = Number(tokenValue);
    const isNumeric = !isNaN(numValue) && !isNaN(numToken);
    switch (operator) {
      case '=': return isNumeric ? numValue === numToken : String(value) === tokenValue;
      case '!=': return isNumeric ? numValue !== numToken : String(value) !== tokenValue;
      case '<': return isNumeric && numValue < numToken;
      case '<=': return isNumeric && numValue <= numToken;
      case '>': return isNumeric && numValue > numToken;
      case '>=': return isNumeric && numValue >= numToken;
      case ':': return String(value).toLowerCase().includes(String(tokenValue).toLowerCase());
      case '!:': return !String(value).toLowerCase().includes(String(tokenValue).toLowerCase());
      default: return String(value).toLowerCase().includes(String(tokenValue).toLowerCase());
    }
  };
  if (operation === 'and') return tokens.every(matcher);
  return tokens.some(matcher);
}

// -- Main component -----------------------------------------------------------

export const BackfillItemsTable: FunctionComponent<BackfillItemsTableProps> = ({
  runId,
  onRowExpanded,
  onCatalogicalLinkClick
}) => {
  // Full-run state
  const [allItems, setAllItems] = useState<BackfillItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [truncated, setTruncated] = useState(false);

  // Expandable rows state
  const [expandedItems, setExpandedItems] = useState<TableItem[]>([]);

  // ResizeObserver: measure wrapper width for full-width detail rows
  const [wrapperWidth, setWrapperWidth] = useState<number | undefined>(undefined);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Fetch the entire run
  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    setExpandedItems([]);
    try {
      const response: GetRunItemsResponse = await ContentBackfillService.instance.getRunItems(runId);
      setAllItems(response.items);
      setTotalCount(response.totalCount);
      setTruncated(response.truncated === true);
    } catch (e: any) {
      setError(e.message || getString('contentBackfill.itemsTable.errorHeader'));
      setAllItems([]);
      setTotalCount(0);
      setTruncated(false);
    } finally {
      setLoading(false);
    }
  }, [runId]);

  // Preferences (shared hook)
  const { currentPreferences, onPreferencesConfirmHandler } = usePreferences({
    preferencesKey: PREFERENCES_KEY,
    defaultPaging: DEFAULT_PAGE_SIZE,
    columnsSettings: columns,
    refreshData: fetchItems
  });

  // Observe the wrapper's visible width so detail rows can match it
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWrapperWidth(entry.contentRect.width);
      }
    });
    observer.observe(el);
    return () => { observer.disconnect(); };
  }, []);

  // Fetch when runId changes (initial fetch handled by usePreferences.refreshData on mount)
  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // -- Cell type renderers (config-driven via table-utils) ---------------------
  const cellTypeRenderers: CellTypeRenderers<TableItem> = useMemo(() => ({
    status_with_tooltip: {
      getIndicatorType: getItemStatusIndicatorType,
      getTooltip: getItemStatusTooltip,
      nullDisplay: getString('contentBackfill.status.na')
    },
    validation_badges: {
      subKeys: ['membership', 'accuracy', 'classification', 'style'],
      getBadgeColor: undefined,
      getBadgeLabel: undefined
    },
    entity_link: {
      linkField: '$.importResult.taxonomyId',
      buildUrl: buildCatalogicalUrl,
      ariaLabel: getString('contentBackfill.itemsTable.viewInCatalogical'),
      onFollow: (item: TableItem) => {
        onCatalogicalLinkClick?.(item.itemId);
      },
      detailRowRenderer: (item: TableItem) => {
        return <DetailRowContent item={item._parentItem!} detailWidth={wrapperWidth} />;
      }
    }
  }), [onCatalogicalLinkClick, wrapperWidth]);

  // -- useCollection: client-side sort + PropertyFilter + pagination -----------
  const {
    items: paginatedItems,
    collectionProps,
    paginationProps,
    propertyFilterProps,
    filteredItemsCount
  } = useCollection<BackfillItem>(allItems, {
    propertyFiltering: {
      filteringProperties: FILTERING_PROPERTIES,
      filteringFunction: itemsFilteringFunction,
      empty: <EmptyTableBox />,
      noMatch: (
        <Box textAlign="center" color="inherit">
          <b>{getString('contentBackfill.textFilter.noMatchTitle')}</b>
          <Box padding={{ bottom: 's' }} variant="div" color="inherit">
            {getString('contentBackfill.textFilter.noMatchDescription')}
          </Box>
        </Box>
      )
    },
    sorting: {
      defaultState: { isDescending: false, sortingColumn: { sortingField: 'name' } }
    },
    pagination: { pageSize: currentPreferences.pageSize },
    selection: { trackBy: 'sourceSourceId' }
  });

  // -- Column definitions: single-pass via buildColumnDefinitions with cellRenderers
  const currentColumnDefinitions: TableProps.ColumnDefinition<TableItem>[] = useMemo(() => {
    const options: BuildColumnOptions<TableItem> = {
      cellRenderers: cellTypeRenderers,
      isDetailRow: (item) => item._isDetailRow === true
    };
    return buildColumnDefinitions(
      columns,
      currentPreferences.visibleContent as readonly string[],
      options
    ) as TableProps.ColumnDefinition<TableItem>[];
  }, [currentPreferences.visibleContent, cellTypeRenderers]);

  // -- Expandable rows handlers ------------------------------------------------
  const handleExpandableItemToggle: TableProps.OnExpandableItemToggle<TableItem> = ({ detail }) => {
    const item = detail.item;
    if (detail.expanded) {
      setExpandedItems(prev => [...prev, item]);
      onRowExpanded?.(item.sourceSourceId);
    } else {
      setExpandedItems(prev => prev.filter(i => i.sourceSourceId !== item.sourceSourceId));
    }
  };

  return (
    <div className="backfill-items-table-wrapper" ref={wrapperRef}>
      {error && (
        <Alert
          type="error"
          header={getString('contentBackfill.itemsTable.errorHeader')}
          data-testid="backfill-items-error-alert"
        >
          {error}
        </Alert>
      )}
      {truncated && (
        <Alert type="warning" data-testid="backfill-items-truncated-alert">
          {getString('contentBackfill.itemsTable.truncatedAlert', { count: String(allItems.length), total: String(totalCount) })}
        </Alert>
      )}
      <Table<TableItem>
        {...collectionProps}
        data-testid="backfill-items-table"
        loading={loading}
        loadingText={getString('contentBackfill.itemsTable.loadingItems')}
        items={paginatedItems as TableItem[]}
        columnDefinitions={currentColumnDefinitions}
        trackBy="sourceSourceId"
        header={
          <Header
            variant="h3"
            counter={`(${filteredItemsCount !== undefined ? filteredItemsCount : allItems.length}/${totalCount})`}
          >
            {getString('contentBackfill.itemsTable.headerTitle')}
          </Header>
        }
        filter={
          <PropertyFilter
            {...propertyFilterProps}
            i18nStrings={getPropertyFilterString()}
            data-testid="property-filter"
          />
        }
        pagination={<Pagination {...paginationProps} />}
        preferences={
          <CollectionPreferences
            title={getString('common.preferences.title')}
            confirmLabel={getString('common.preferences.confirmLabel')}
            cancelLabel={getString('common.preferences.cancelLabel')}
            pageSizePreference={{
              title: getString('common.preferences.pageSize.title'),
              options: defaultPagingOptions
            }}
            stripedRowsPreference={{
              label: getString('common.preferences.stripedRows.label'),
              description: getString('common.preferences.stripedRows.description')
            }}
            visibleContentPreference={{
              title: getString('common.preferences.visibleContentPreference.title'),
              options: [
                {
                  label: getString('common.preferences.visibleContentPreference.columns'),
                  options: PREFS_CONFIG.visibleContentOptions
                }
              ]
            }}
            preferences={currentPreferences}
            onConfirm={onPreferencesConfirmHandler}
          />
        }
        expandableRows={{
          getItemChildren: (item: TableItem) => {
            if (item._isDetailRow) return [];
            return [{
              ...item,
              _isDetailRow: true,
              _parentItem: item,
              sourceSourceId: `${item.sourceSourceId}#detail`
            } as TableItem];
          },
          isItemExpandable: (item: TableItem) => !item._isDetailRow,
          expandedItems,
          onExpandableItemToggle: handleExpandableItemToggle
        }}
        empty={<EmptyTableBox />}
        variant="borderless"
        stripedRows={currentPreferences.stripedRows}
        resizableColumns
      />
    </div>
  );
};
